import * as React from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { useToast as useToastAria, useToastRegion } from 'react-aria';

import { IconNotification } from '@/components/application/notifications/notifications';
import { Button } from '@/components/base/buttons/button';
import { dismissToast, toast as enqueueToast, useToast } from '@/hooks/use-toast';
import { cx } from '@/lib/utils/cx';

import { useToastTimers } from './toast-timers';

import type { ToasterToast } from '@/hooks/use-toast';
import type { QueuedToast, ToastState } from 'react-stately';

/**
 * Distance a toast must travel before releasing the pointer dismisses it rather
 * than springing back. Below this, the drag reads as a scroll or a mis-grab.
 */
const SWIPE_DISMISS_THRESHOLD_PX = 56;

/**
 * Caps each of the toast's two text lines at two visual lines. A toast is a
 * glance, not a document: past this the message belongs in the UI it came from.
 * `break-words` keeps an unbroken token (a URL, an id) inside the card.
 */
const TRUNCATED_TEXT_CLASS = 'line-clamp-2 break-words';

/**
 * The expanded message is still bounded: a toast that grew past the viewport
 * would be clipped by the region's own bottom anchoring, with the start of the
 * message the part that disappears.
 */
const EXPANDED_TEXT_CLASS = 'line-clamp-[12] break-words';

/** Elements that own their own pointer semantics and must never start a swipe. */
const INTERACTIVE_SELECTOR = 'a, button, input, select, textarea, [role="button"]';

export interface ToastProps {
  variant?: 'default' | 'destructive';
  /** Milliseconds before the toast auto-dismisses. Defaults to 5000. */
  duration?: number;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

export interface ToastActionProps {
  /** Overrides the accessible name when the visible label is not descriptive alone. */
  readonly 'aria-label'?: string;
  readonly children?: React.ReactNode;
  readonly className?: string;
  readonly onClick?: React.MouseEventHandler<HTMLButtonElement>;
}

/**
 * A caller-supplied call to action. It sits in the card's own action row, so it
 * matches the link treatment of the card's dismiss button rather than competing
 * with it. Grey, not brand: a toast CTA is a way back from something that just
 * happened, not the primary path onward.
 */
export const ToastAction = ({
  'aria-label': ariaLabel,
  children,
  className,
  onClick,
}: ToastActionProps) => (
  <Button
    aria-label={ariaLabel}
    className={className}
    color="link-gray"
    size="sm"
    onClick={onClick}
  >
    {children}
  </Button>
);

export type ToastActionElement = React.ReactElement<ToastActionProps>;

/**
 * `IconNotification` declares `title`/`description` as `string`, but only ever
 * renders them as paragraph children, so any `ReactNode` is valid at runtime.
 * The toast store has always carried `ReactNode` for both. One widened alias
 * here beats a cast at every field.
 */
const Notification = IconNotification as React.FC<
  Omit<React.ComponentProps<typeof IconNotification>, 'title' | 'description'> & {
    title: React.ReactNode;
    description: React.ReactNode;
  }
>;

interface ToastItemProps {
  readonly queued: QueuedToast<ToasterToast>;
  readonly state: ToastState<ToasterToast>;
  /** Suspends the toast's auto-dismiss timer until `onRelease`. */
  readonly onHold: (id: string) => void;
  readonly onRelease: (id: string) => void;
}

function ToastItem({ queued, state, onHold, onRelease }: ToastItemProps) {
  const { t } = useTranslation('common');
  const ref = React.useRef<HTMLDivElement>(null);
  const leadRef = React.useRef<HTMLSpanElement>(null);
  const supportingRef = React.useRef<HTMLSpanElement>(null);
  const { toastProps, contentProps, titleProps, descriptionProps } = useToastAria(
    { toast: queued },
    state,
    ref,
  );

  const dragOriginRef = React.useRef<number | null>(null);
  const [dragOffset, setDragOffset] = React.useState(0);

  const toast = queued.content;
  const isOpen = toast.open !== false;

  const handlePointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
    if (event.target instanceof Element && event.target.closest(INTERACTIVE_SELECTOR)) {
      return;
    }
    dragOriginRef.current = event.clientY;
    event.currentTarget.setPointerCapture?.(event.pointerId);
  };

  const handlePointerMove = (event: React.PointerEvent<HTMLDivElement>) => {
    if (dragOriginRef.current === null) {
      return;
    }
    setDragOffset(Math.max(0, event.clientY - dragOriginRef.current));
  };

  const handlePointerEnd = (event: React.PointerEvent<HTMLDivElement>) => {
    if (dragOriginRef.current === null) {
      return;
    }
    const travelled = Math.max(0, event.clientY - dragOriginRef.current);
    dragOriginRef.current = null;
    event.currentTarget.releasePointerCapture?.(event.pointerId);
    setDragOffset(0);
    if (travelled >= SWIPE_DISMISS_THRESHOLD_PX) {
      state.close(queued.key);
    }
  };

  // A toast may carry either field alone. The lead line is whichever is
  // present; the secondary line only exists when both are.
  const leadText = toast.title ?? toast.description;
  const supportingText = toast.title ? toast.description : undefined;

  const [isExpanded, setIsExpanded] = React.useState(false);
  const [isClamped, setIsClamped] = React.useState(false);

  // Whether the clamp is actually hiding anything can only be known once the
  // text has been laid out at the card's width, so the toggle is offered from
  // measurement rather than from a guess at the message's length. The 1px
  // tolerance absorbs sub-pixel rounding (scrollHeight rounds up, clientHeight
  // down), which otherwise offers the toggle on a message that fits; a real
  // clamp hides a whole line, far past 1px. Re-measured once the web font
  // loads — the fallback face wraps to a different line count. Matches
  // ad-copy-caption.tsx.
  React.useLayoutEffect(() => {
    if (isExpanded) {
      return;
    }
    const measure = () => {
      setIsClamped(
        [leadRef.current, supportingRef.current].some(
          (element) => element !== null && element.scrollHeight > element.clientHeight + 1,
        ),
      );
    };
    measure();
    let cancelled = false;
    // document.fonts is absent under jsdom; guard so the measurement still runs.
    if (document.fonts) {
      document.fonts.ready
        .then(() => {
          if (!cancelled) {
            measure();
          }
        })
        .catch(() => {
          /* font-load failure leaves the fallback measurement in place */
        });
    }
    return () => {
      cancelled = true;
    };
  }, [isExpanded, leadText, supportingText]);

  const toggleExpanded = () => {
    const willExpand = !isExpanded;
    if (willExpand) {
      onHold(toast.id);
    } else {
      onRelease(toast.id);
    }
    setIsExpanded(willExpand);
  };

  const textClass = isExpanded ? EXPANDED_TEXT_CLASS : TRUNCATED_TEXT_CLASS;

  const expandAction =
    isClamped || isExpanded ? (
      <ToastAction onClick={toggleExpanded}>
        {t(isExpanded ? 'notifications.showLess' : 'notifications.showMore')}
      </ToastAction>
    ) : null;

  const card = (
    <Notification
      hideDismissLabel
      action={
        expandAction ? (
          <>
            {expandAction}
            {toast.action}
          </>
        ) : (
          toast.action
        )
      }
      color={toast.variant === 'destructive' ? 'error' : 'success'}
      description={
        supportingText ? (
          <span {...descriptionProps} ref={supportingRef} className={textClass}>
            {supportingText}
          </span>
        ) : (
          ''
        )
      }
      // Also names the card's close button, which would otherwise carry
      // the component's hardcoded English default.
      dismissLabel={t('notifications.dismiss')}
      title={
        <span {...titleProps} ref={leadRef} className={textClass}>
          {leadText}
        </span>
      }
      onClose={() => state.close(queued.key)}
    />
  );

  return (
    <div
      {...toastProps}
      ref={ref}
      className={cx(
        // `touch-pan-x` hands vertical gestures to the swipe handler instead of
        // letting the browser consume them as a scroll; `touch-pinch-zoom` keeps
        // zoom available, which a bare `touch-action: none` would remove.
        // A fixed card width keeps a long message from stretching the toast
        // across the viewport; the text inside clamps rather than reflowing.
        'pointer-events-auto w-100 max-w-full touch-pan-x touch-pinch-zoom outline-none',
        isOpen
          ? 'animate-in fade-in-0 slide-in-from-bottom-3'
          : // The store keeps a dismissed toast mounted for its removal delay,
            // which outlasts the exit animation. Without `fill-mode-forwards` the
            // toast springs back to full opacity and its resting position the
            // moment the animation ends, reading as a second bounce; without
            // `pointer-events-none` it keeps swallowing clicks while invisible.
            'pointer-events-none animate-out fade-out-0 fill-mode-forwards slide-out-to-bottom-3',
      )}
      style={
        dragOffset > 0
          ? { transform: `translateY(${dragOffset}px)`, transition: 'none' }
          : undefined
      }
      onPointerCancel={handlePointerEnd}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerEnd}
    >
      <div {...contentProps}>{card}</div>
    </div>
  );
}

function ToastRegion({ toasts }: { readonly toasts: ToasterToast[] }) {
  const { t } = useTranslation('common');
  const ref = React.useRef<HTMLDivElement>(null);
  const { pauseAll, resumeAll, hold, release } = useToastTimers(toasts);

  const visibleToasts = React.useMemo<QueuedToast<ToasterToast>[]>(
    () => toasts.map((toast) => ({ key: toast.id, content: toast })),
    [toasts],
  );

  const state = React.useMemo<ToastState<ToasterToast>>(
    () => ({
      visibleToasts,
      add: (content) => enqueueToast(content).id,
      close: dismissToast,
      pauseAll,
      resumeAll,
    }),
    [visibleToasts, pauseAll, resumeAll],
  );

  const { regionProps } = useToastRegion({ 'aria-label': t('notifications.title') }, state, ref);

  return (
    <div
      {...regionProps}
      ref={ref}
      // Anchored to the viewport's bottom-right corner, floated off it by a
      // gutter. The region is portaled to `document.body`, so the gutter is
      // measured from the page edge and stays put as the app layout changes.
      // It is added to the device safe area rather than replacing it, so a
      // notched device clears its inset and still gets the full gutter.
      className={cx(
        'pointer-events-none fixed right-0 bottom-0 z-[100010] flex w-full flex-col items-end gap-2',
        'pr-[calc(env(safe-area-inset-right)+16px)] pb-[calc(env(safe-area-inset-bottom)+16px)] pl-[calc(env(safe-area-inset-left)+16px)]',
      )}
    >
      {visibleToasts.map((queued) => (
        <ToastItem
          key={queued.key}
          queued={queued}
          state={state}
          onHold={hold}
          onRelease={release}
        />
      ))}
    </div>
  );
}

/**
 * Mounts the toast host. Rendered into `document.body` so toasts stack above
 * dialogs, which own their own stacking context.
 */
export function Toaster() {
  const { toasts } = useToast();
  const [isMounted, setIsMounted] = React.useState(false);

  React.useEffect(() => {
    setIsMounted(true);
  }, []);

  if (!isMounted || toasts.length === 0) {
    return null;
  }

  return createPortal(<ToastRegion toasts={toasts} />, document.body);
}
